import { Box, Typography, Card, CardContent, Chip, Divider, CircularProgress, Alert } from '@mui/material';
import { CalendarMonth, EventBusy, AccessTime } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { getTeacherTodaySchedule } from '@/api/teacher-api';

const DAYS: Record<number, string> = { 0: 'SUNDAY', 1: 'MONDAY', 2: 'TUESDAY', 3: 'WEDNESDAY', 4: 'THURSDAY', 5: 'FRIDAY', 6: 'SATURDAY' };

export function TeacherDashboardPage() {
  const user = useAuthStore((state) => state.user);
  const today = new Date();
  const dayOfWeek = DAYS[today.getDay()];
  const dateLabel = today.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['teacher-today', user?.id],
    queryFn: () => getTeacherTodaySchedule(user!.id),
    enabled: !!user?.id,
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Good {getGreeting()}, {user?.name?.split(' ')[0] ?? 'Teacher'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{dateLabel}</Typography>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Could not load today's schedule.</Alert>}

      {data && (
        <>
          {data.periods.length === 0 ? (
            <Alert severity="info" icon={<CalendarMonth />}>No classes scheduled for today.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Today's Schedule — {data.periods.length} period{data.periods.length !== 1 ? 's' : ''}
              </Typography>
              {data.periods.map((p, i) => (
                <Card key={i} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {p.subject_name ?? 'No Subject'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {p.class_name} {p.section}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip
                          size="small"
                          icon={<AccessTime sx={{ fontSize: 14 }} />}
                          label={`${p.time_start} – ${p.time_end}`}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Period {p.period_number}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CalendarMonth color="primary" fontSize="small" />
                  <Typography variant="subtitle2">Today's Periods</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{data.periods.length}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EventBusy color="warning" fontSize="small" />
                  <Typography variant="subtitle2">Active Absences</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{data.activeAbsences}</Typography>
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
